/**
 * Patient media (260903) — typed client for
 * backend/app/Http/Controllers/Api/V1/LeadMediaController.
 *
 * Everything attached to a patient is one collection filed into folders:
 * before/after photos, x-rays, tickets, identity papers. The folder is not a
 * label — it is what authorization is expressed in, so the index returns only
 * the folders this caller may read and never names the rest.
 *
 * Replaces both `patient-photos-api` and the lead preset over the generic
 * attachments client.
 */
import api from "./api";
import type {
  ApiResponse,
  LeadMediaFolder,
  LeadMediaIndex,
  LeadMediaItem,
  LeadWhatsAppMediaItem,
  LeadWhatsAppMediaPage,
  Payment,
} from "@/types";

export const listLeadMedia = async (
  leadId: string,
): Promise<LeadMediaIndex> => {
  const response = await api.get<ApiResponse<LeadMediaIndex>>(
    `/leads/${leadId}/media`,
  );
  return response.data.data;
};

/**
 * @param paymentId ArtDent 260904 — the payment this file evidences. Optional;
 *   most uploads are ordinary paperwork. The server validates that the payment
 *   belongs to this patient, so a bad id is a 422 rather than a mis-filing.
 * @param customName The DISPLAY name for the file (`media.name`), which the
 *   gallery shows in place of the stored filename. Pasted files need it: the
 *   clipboard names every image `image.png`, so the folder would otherwise
 *   fill with rows nobody can tell apart.
 */
export const uploadLeadMedia = async (
  leadId: string,
  file: File,
  folder: string,
  paymentId?: string,
  customName?: string,
  /**
   * ArtDent 261007 — 0–100 as the bytes leave. A video can take minutes on a
   * hotel's wifi, and a spinner alone reads as a hang.
   */
  onProgress?: (percent: number) => void,
): Promise<LeadMediaItem> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("folder", folder);
  if (paymentId) formData.append("payment_id", paymentId);
  if (customName) formData.append("custom_name", customName);

  const response = await api.post<ApiResponse<LeadMediaItem>>(
    `/leads/${leadId}/media`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: onProgress
        ? (event) => {
            if (event.total) onProgress(Math.round((event.loaded / event.total) * 100));
          }
        : undefined,
    },
  );
  return response.data.data;
};

/**
 * ArtDent 261007 — a 10-minute signed link a `<video>` can play and seek.
 *
 * The download route needs the auth header, which a `<video src>` cannot send;
 * this one is relative and same-origin, and answers Range requests.
 */
export const getLeadMediaStreamUrl = async (
  leadId: string,
  mediaId: number,
): Promise<string> => {
  const response = await api.get<ApiResponse<{ url: string; expires_at: string }>>(
    `/leads/${leadId}/media/${mediaId}/stream-url`,
  );
  return response.data.data.url;
};

/** By name or by type — a phone's picker does not always fill in `type`. */
export const isVideoFile = (file: File): boolean =>
  file.type.startsWith("video/") || /\.(mp4|mov|webm)$/i.test(file.name);

/** What a video-taking folder adds to its picker (mirrors MediaRules). */
export const VIDEO_ACCEPT = ".mp4,.mov,.webm,video/mp4,video/quicktime,video/webm";

/**
 * ArtDent 260929 — file a document against one payment and learn what that did
 * to it. On a clinic where the bank's confirmation receives a pending transfer
 * the answer is `received`; `pending` means the file is kept and the transfer
 * still waits for Confirm received funds.
 */
export const uploadPaymentProof = async (
  leadId: string,
  file: File,
  paymentId: string,
): Promise<{ media: LeadMediaItem; settlementStatus: Payment["settlement_status"] | null }> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("folder", "payment_proofs");
  formData.append("payment_id", paymentId);

  const response = await api.post<
    ApiResponse<LeadMediaItem> & { payment_settlement_status?: Payment["settlement_status"] | null }
  >(`/leads/${leadId}/media`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return {
    media: response.data.data,
    settlementStatus: response.data.payment_settlement_status ?? null,
  };
};

export const deleteLeadMedia = async (
  leadId: string,
  mediaId: number,
): Promise<void> => {
  await api.delete(`/leads/${leadId}/media/${mediaId}`);
};

/**
 * The bytes, as an object URL the browser can put in an `<img src>`.
 *
 * These routes are authorised like every other API call — a Bearer token from
 * the axios interceptor — so a plain `<img src={item.url}>` would fetch
 * unauthenticated and 401. The alternative, serving patient faces off the
 * public disk the way doctor headshots are, would make every file readable by
 * anyone holding the URL. So the caller fetches the blob and owns its lifetime:
 * revoke the returned URL when the tile unmounts.
 */
export const fetchLeadMediaObjectUrl = async (
  leadId: string,
  mediaId: number,
  variant: "thumbnail" | "download" = "thumbnail",
  /**
   * Types the blob. The browser picks its PDF viewer from the blob's type, and
   * an untyped blob in an iframe or a new tab is offered as a download instead
   * of shown (260930 — the in-app PDF preview needs this).
   */
  mimeType?: string,
): Promise<string> => {
  const response = await api.get<Blob>(`/leads/${leadId}/media/${mediaId}/${variant}`, {
    responseType: "blob",
  });

  const type = mimeType || response.data.type || "";
  return window.URL.createObjectURL(new Blob([response.data], { type }));
};

/**
 * Triggers a browser save for a file that is not an image.
 *
 * Same blob dance as above — the download route needs the auth header, so an
 * `<a href>` straight at it would 401.
 */
export const downloadLeadMedia = async (
  leadId: string,
  item: LeadMediaItem,
): Promise<void> => {
  const url = await fetchLeadMediaObjectUrl(leadId, item.id, "download");
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", item.file_name);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * File an attachment that arrived on WhatsApp into one of this patient's
 * folders (260909).
 *
 * A server-side COPY of bytes the app already holds — the browser never carries
 * the image, so the original quality and filename survive, non-image
 * attachments work, and nothing depends on what a given browser is willing to
 * put on a clipboard. The WhatsApp message keeps its own copy.
 *
 * Filing the same message into the same folder twice is a double-click, not a
 * second file: the server hands back the row it already has.
 */
/**
 * The photos and documents on the patient's WhatsApp threads (260922) — the
 * read side of `saveWhatsAppMediaToLead`. Nothing is copied: each item is a
 * message, and its bytes load through the message media route. The server has
 * already left out threads this caller may not open.
 */
export const listLeadWhatsAppMedia = async (
  leadId: string,
  page = 1,
  perPage = 60,
): Promise<LeadWhatsAppMediaPage> => {
  const response = await api.get<{
    data: LeadWhatsAppMediaItem[];
    meta: Omit<LeadWhatsAppMediaPage, "items">;
  }>(`/leads/${leadId}/media/from-whatsapp`, { params: { page, per_page: perPage } });
  return { items: response.data.data, ...response.data.meta };
};

export const saveWhatsAppMediaToLead = async (
  leadId: string,
  messageId: string,
  folder: string,
  customName?: string,
): Promise<LeadMediaItem> => {
  const response = await api.post<ApiResponse<LeadMediaItem>>(
    `/leads/${leadId}/media/from-whatsapp`,
    { message_id: messageId, folder, custom_name: customName || undefined },
  );
  return response.data.data;
};

/**
 * Create a folder on the patient's files rail (260914).
 *
 * `permission` is the section the folder belongs to, and it is OPTIONAL on
 * purpose: a caller who can upload to exactly one section — the `marketing`
 * photographer, the `radiographer` — never has to answer the question, because
 * the server infers it. Someone who can upload to several (admin) must name it,
 * and naming one they cannot upload to is a 422. The server is the authority
 * either way; this argument only saves it a guess.
 */
export const createLeadMediaFolder = async (input: {
  label: string;
  accepts: "image" | "file";
  permission?: string;
  /**
   * The folder to nest inside. When given, the server FORCES the new folder's
   * permission to the parent's, whatever `permission` says — a subfolder can
   * never be a wider door than the folder it is drawn inside.
   */
  parent?: string;
}): Promise<LeadMediaFolder> => {
  const response = await api.post<ApiResponse<LeadMediaFolder>>(
    "/lead-media-folders",
    {
      label: input.label,
      accepts: input.accepts,
      permission: input.permission || undefined,
      parent: input.parent || undefined,
    },
  );
  return response.data.data;
};
